import { Routes } from "@angular/router";
import {
  actionsLazyRoutes,
  RouteKey as ActionsRouteKey,
  routes as actionsRoutes,
} from "src/app/pages/actions-page/actions.routing";
import { RouteKey as ArticlesRouteKey, routes as articleRoutes } from "src/app/pages/articles-page/routes";
import {
  RouteKey as InvestmentOptionsRouteKey,
  routes as investmentOptionsRoutes,
} from "src/app/pages/investment-options-page/routes";
import {
  needsLazyRoutes,
  RouteKey as NeedsRouteKey,
  routes as needsRoutes,
} from "src/app/pages/needs-page/needs.routing";
import {
  managePkbPageLazyRoutes,
  RouteKey as ManagePkbRouteKey,
  routes as managePkbRoutes,
} from "src/app/pages/manage-pkb-page/manage-pkb.routing";
import {
  RouteKey as PlanRouteKey,
  deprecatedRoutes as planDeprecatedRoutes,
  routes as planRoutes,
} from "src/app/modules/pension-plan/routes";
import {
  RouteKey as SmartAccountRouteKey,
  routes as smartAccountRoutes,
} from "src/app/pages/smart-account-page/routes";
import { navigateToPensionPlanResolver } from "src/app/resolvers/navigate-to-pension-plan.resolver";
import { environment } from "src/environments/environment";
import { canActivateApp } from "./guards/auth.guard";
import { smartAccountResolver } from "./pages/smart-account-page/resolvers/smart-account.resolver";
import { apiHomeResolver } from "./resolvers/app.resolver";
import {
  afpResolver,
  bankResolver,
  navResolver,
  norskpensjonResolver,
  publicPensionResolver,
  savingsAndPensionResolver,
  unavailableNorskpensjonResolver,
} from "./resolvers/engagement.resolver";
import { removeAfpInPublicContextResolver } from "./resolvers/remove-afp-in-public-context.resolver";
import {
  consentResolver,
  customerResolver,
  customerSuppliedClientDataResolver,
  customerSuppliedDataResolver,
} from "./resolvers/user.resolver";
import { RouteKey, routes } from "./routes";
import {
  RouteKey as SavingForPensionKey,
  routes as savingForPensionRoutes,
} from "src/app/pages/saving-for-pension-page/routes";
import { pensionPlanLazyRoutes } from "src/app/modules/pension-plan/pension-plan.routing";
import { smartAccountLazyRoutes } from "src/app/pages/smart-account-page/smart-account.routing";
import { investmentOptionsLazyRoutes } from "src/app/pages/investment-options-page/investment-options.routing";
import { articlesLazyRoutes } from "src/app/pages/articles-page/articles.routing";
import { savingForPensionLazyRoutes } from "src/app/pages/saving-for-pension-page/saving-for-pension.routing";
import { PageNotFoundComponent } from "src/app/pages/pagenotfound/pagenotfound.component";
import { contractsLazyRouting } from "src/app/v2/pages/contracts/contracts.routing";

const smartPensjonBreadcrumb = "breadcrumbs.smartPension";

/**
 * Renamed or deprecated routes for backward compability.
 * A visit to any route in this list will trigger a monitor warning, which is
 * useful for catching old links spread around both inside and outside
 * the application.
 */
export const monitoredDeprecatedUrls: string[] = Object.values(planDeprecatedRoutes).map(
  (value) => `${planRoutes[PlanRouteKey.Root]}/${value}`,
);

export const appRoutes: Routes = [
  {
    path: "",
    children: [
      {
        path: "",
        resolve: {
          apiHomeResolver,
        },
        children: [
          {
            path: "",
            canActivateChild: environment.offlineMode ? [] : [canActivateApp],
            children: [
              {
                /**
                 * The dashboard runs none of the customer, consent, customer-supplied or engagement resolvers.
                 * It reads client data only for ?q=beregning and the pension plan card. ensureLoaded() loads it
                 * on demand, and first reads the storage consent from GET /user/consents.
                 * It is the first child, so the empty-path routes below cannot capture "/".
                 */
                path: routes[RouteKey.Root],
                pathMatch: "full",
                resolve: { navigateToPensionPlanResolver },
                loadComponent: () =>
                  import("./pages/dashboard-page/dashboard-page.component").then((c) => c.DashboardPageComponent),
                data: {
                  breadcrumb: smartPensjonBreadcrumb,
                },
              },
              {
                path: "",
                /**
                 * Resolvers in one `resolve` map run in parallel; route levels run serially.
                 * These have no data dependency, so splitting them across levels would
                 * serialise independent requests on every cold load. See app.routing.spec.ts.
                 * smartAccountResolver is fire-and-forget, so it adds no wait. It starts the Smartkonto lookup for
                 * the shell pages. The dashboard reads the REST Smartkonto status instead, so a cold / makes no
                 * GraphQL call.
                 */
                resolve: {
                  smartAccountResolver,
                  customerResolver,
                  consentResolver,
                },
                children: [
                  {
                    path: "",
                    resolve: {
                      customerSuppliedDataResolver,
                      customerSuppliedClientDataResolver,
                    },
                    children: [
                      {
                        path: "",
                        resolve: {
                          removeAfpInPublicContextResolver,
                          afpResolver,
                          bankResolver,
                          navResolver,
                          norskpensjonResolver,
                          publicPensionResolver,
                          savingsAndPensionResolver,
                          unavailableNorskpensjonResolver,
                        },
                        children: [
                          {
                            path: routes[RouteKey.Root],
                            data: {
                              breadcrumb: smartPensjonBreadcrumb,
                            },
                            children: [
                              {
                                path: routes[RouteKey.ContactUs],
                                // deprecated contact us page, move user to Articles page
                                redirectTo: "/nyttig-om-pensjon",
                                pathMatch: "full",
                              },
                              {
                                path: routes[RouteKey.Concept],
                                loadComponent: () =>
                                  import("./pages/concept-page/concept-page.component").then(
                                    (c) => c.ConceptPageComponent,
                                  ),
                                data: {
                                  breadcrumb: "breadcrumbs.concept",
                                },
                              },
                              {
                                path: planRoutes[PlanRouteKey.Root],
                                children: pensionPlanLazyRoutes,
                                data: {
                                  breadcrumb: "skip-breadcrumb",
                                },
                              },
                              {
                                path: smartAccountRoutes[SmartAccountRouteKey.Root],
                                children: smartAccountLazyRoutes,
                                data: {
                                  breadcrumb: "breadcrumbs.smartAccount",
                                },
                              },
                              {
                                path: investmentOptionsRoutes[InvestmentOptionsRouteKey.Root],
                                children: investmentOptionsLazyRoutes,
                                data: {
                                  breadcrumb: "breadcrumbs.investmentOptions",
                                },
                              },
                              {
                                path: articleRoutes[ArticlesRouteKey.Root],
                                children: articlesLazyRoutes,
                                data: {
                                  breadcrumb: "breadcrumbs.faq",
                                },
                              },
                              {
                                path: actionsRoutes[ActionsRouteKey.Root],
                                children: actionsLazyRoutes,
                                data: {
                                  breadcrumb: "breadcrumbs.actions",
                                },
                              },
                              {
                                path: needsRoutes[NeedsRouteKey.Root],
                                children: needsLazyRoutes,
                                data: {
                                  breadcrumb: "breadcrumbs.needs",
                                },
                              },
                              {
                                path: managePkbRoutes[ManagePkbRouteKey.Root],
                                children: managePkbPageLazyRoutes,
                                data: {
                                  breadcrumb: "breadcrumbs.managePkb",
                                },
                              },
                              {
                                path: savingForPensionRoutes[SavingForPensionKey.Root],
                                children: savingForPensionLazyRoutes,
                                data: {
                                  breadcrumb: "breadcrumbs.savingForPension",
                                },
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: "avtaler",
                resolve: {
                  consentResolver,
                },
                data: {
                  breadcrumb: smartPensjonBreadcrumb,
                },
                children: [
                  {
                    path: "",
                    data: {
                      breadcrumb: "breadcrumbs.contracts",
                    },
                    children: contractsLazyRouting,
                  },
                ],
              },
              {
                path: "**",
                component: PageNotFoundComponent,
                data: {
                  breadcrumb: "breadcrumbs.pageNotFound",
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
